import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import RetreatApi from "../../../../api/retreatApi";
import AdminApi from "../../../../api/adminApi";
import AuthApi from "../../../../api/authApi";
import ParishApi from "../../../../api/parishApi";
import CommitteeApi from "../../../../api/committeeApi";
import { getAuthUser } from "../../../../hooks/useAuthUser";
import { formatCoupleOptionLabel } from "../../../../utils/coupleLabels";
import {
  BUDGET_LINE_KEYS,
  ELIGIBILITY_ITEMS,
  SUPPORT_LABELS,
  SUPPORT_TYPES,
  emptyBudget,
  emptyEligibility,
  type CoupleOption,
  type CoupleRow,
  type OversightContactOption,
  type ParishRow,
  type PlanningSupportRow,
  type Season,
  type SupportPerson,
  type SupportTypeCode,
} from "./projectedRetreatConstants";
import {
  actorPayload,
  addDays,
  calendarDateString,
  contactKindForSupportType,
  contactOptionValue,
  coupleShortName,
  flattenSteeringMembers,
  flattenSuperAdmins,
  formatPersonName,
  formatPersonOptionLabel,
  formatWeekendRange,
  oversightContactHelper,
  oversightContactPlaceholder,
  oversightOptionsForType,
  oversightSectionSubtitle,
  parishDisplayName,
  parseContactOptionValue,
  parseNyCalendarDate,
  seasonDateRange,
  seasonFromMonth,
  seasonYearFromDate,
  toLocalNoon,
} from "./projectedRetreatHelpers";

export function useProjectedRetreatForm() {
  const navigate = useNavigate();
  const { id: routeId } = useParams();
  const retreatId = routeId ? Number(routeId) : null;
  const userData = getAuthUser<any>();
  const isAdmin = userData?.userRole === "ADMIN";

  const retreatApi = useMemo(() => new RetreatApi(), []);
  const adminApi = useMemo(() => new AdminApi(), []);
  const authApi = useMemo(() => new AuthApi(), []);
  const parishApi = useMemo(() => new ParishApi(), []);
  const committeeApi = useMemo(() => new CommitteeApi(), []);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [transitioning, setTransitioning] = useState(false);
  const [parishes, setParishes] = useState<ParishRow[]>([]);
  const [couples, setCouples] = useState<CoupleRow[]>([]);
  const [steeringMembers, setSteeringMembers] = useState<SupportPerson[]>([]);
  const [superAdmins, setSuperAdmins] = useState<SupportPerson[]>([]);
  const [parishId, setParishId] = useState<number | "">("");
  const [venue, setVenue] = useState("");
  const [language, setLanguage] = useState("English");
  const [retreatType, setRetreatType] = useState<string>("Established Parish");
  const [season, setSeason] = useState<Season | "">("");
  const [year, setYear] = useState<string>("");
  const [tentativeFriday, setTentativeFriday] = useState<Date | null>(null);
  const [tentativeSunday, setTentativeSunday] = useState<Date | null>(null);
  const [dateError, setDateError] = useState("");
  const [dateLocksSeasonYear, setDateLocksSeasonYear] = useState(false);

  const [supportType, setSupportType] = useState<SupportTypeCode>(
    SUPPORT_TYPES.STEERING_COMMITTEE,
  );
  const [pendingContactValue, setPendingContactValue] = useState<string | null>(
    null,
  );
  const [planningSupport, setPlanningSupport] = useState<PlanningSupportRow[]>(
    [],
  );
  const [planningNotes, setPlanningNotes] = useState("");

  const [seedStatus, setSeedStatus] = useState("Not reviewed");
  const [seedAmount, setSeedAmount] = useState("");
  const [seedFundingContact, setSeedFundingContact] = useState("");
  const [seedExecutionContact, setSeedExecutionContact] = useState("");
  const [seedNote, setSeedNote] = useState("");

  const [sponsorshipStatus, setSponsorshipStatus] = useState("Not reviewed");
  const [sponsorshipContact, setSponsorshipContact] = useState("");
  const [sponsorshipExecution, setSponsorshipExecution] = useState("");
  const [sponsorshipNote, setSponsorshipNote] = useState("");
  const [budgetNote, setBudgetNote] = useState("");
  const [budget, setBudget] = useState<Record<string, string>>(emptyBudget);

  const [leadCoupleId, setLeadCoupleId] = useState<number | "">("");
  const [eligibility, setEligibility] = useState(emptyEligibility);
  const [nextStage, setNextStage] = useState("Keep as Projected");
  const [transitionNote, setTransitionNote] = useState("");
  const [notificationMessage, setNotificationMessage] = useState("");
  const [notificationEdited, setNotificationEdited] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [savedPlanningPercent, setSavedPlanningPercent] = useState<number | null>(
    null,
  );

  const [newParishModalOpen, setNewParishModalOpen] = useState(false);
  const [newParishSaving, setNewParishSaving] = useState(false);
  const [newParishForm, setNewParishForm] = useState({
    name: "",
    addressLine1: "",
    city: "",
    state: "TX",
    postalCode: "",
  });
  const [newParishErrors, setNewParishErrors] = useState<Record<string, string>>(
    {},
  );

  const [leadContactModalOpen, setLeadContactModalOpen] = useState(false);
  const [leadContactSaving, setLeadContactSaving] = useState(false);
  const [leadContactForm, setLeadContactForm] = useState({
    hisFirstName: "",
    hisLastName: "",
    hisEmail: "",
    herFirstName: "",
    herLastName: "",
    herEmail: "",
    notes: "",
    address: "",
  });

  const [reportModalOpen, setReportModalOpen] = useState(false);
  const [setupConfirmModalOpen, setSetupConfirmModalOpen] = useState(false);
  const [transitionSuccess, setTransitionSuccess] = useState<{
    parishName: string;
    retreatId: number;
    accessPreparedAt: string | Date;
  } | null>(null);

  const selectedParish = parishes.find((p) => Number(p.id) === Number(parishId));
  const selectedLead = couples.find((c) => Number(c.id) === Number(leadCoupleId));
  const leadAssigned = Boolean(leadCoupleId);

  const coupleOptions: CoupleOption[] = useMemo(
    () =>
      couples.map((c) => ({
        value: Number(c.id),
        label: formatCoupleOptionLabel(c),
        couple: c,
      })),
    [couples],
  );

  const supportPeopleById = useMemo(() => {
    const map = new Map<number, SupportPerson>();
    for (const p of steeringMembers) map.set(p.id, p);
    for (const p of superAdmins) map.set(p.id, p);
    return map;
  }, [steeringMembers, superAdmins]);

  const contactOptionsForType = (
    type: SupportTypeCode | string,
  ): OversightContactOption[] => {
    const kind = contactKindForSupportType(type);
    if (kind === "couple") {
      return coupleOptions.map((o) => ({
        value: contactOptionValue("couple", o.value),
        label: o.label,
        kind: "couple",
        id: o.value,
      }));
    }
    if (type === SUPPORT_TYPES.STEERING_COMMITTEE) {
      if (!parishId) return [];
      return steeringMembers
        .filter((m) => Number(m.parishId) === Number(parishId))
        .map((m) => ({
          value: contactOptionValue("user", m.id),
          label: formatPersonOptionLabel(m),
          kind: "user" as const,
          id: m.id,
        }));
    }
    return superAdmins.map((m) => ({
      value: contactOptionValue("user", m.id),
      label: formatPersonOptionLabel(m),
      kind: "user" as const,
      id: m.id,
    }));
  };

  const contactOptions = useMemo(
    () => contactOptionsForType(supportType),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [supportType, parishId, coupleOptions, steeringMembers, superAdmins],
  );

  const resolveSupportName = (row?: PlanningSupportRow | null) => {
    if (!row) return "";
    if (row.userId) {
      const person = supportPeopleById.get(Number(row.userId));
      if (person) return formatPersonName(person);
      return row.coupleName || `User #${row.userId}`;
    }
    const couple = couples.find((c) => Number(c.id) === Number(row.coupleId));
    if (couple) return coupleShortName(couple);
    return row.coupleName || (row.coupleId ? `Couple #${row.coupleId}` : "");
  };

  const resolveSupportEmail = (row?: PlanningSupportRow | null) => {
    if (!row) return "";
    if (row.userId) {
      return supportPeopleById.get(Number(row.userId))?.email || "";
    }
    const couple = couples.find((c) => Number(c.id) === Number(row.coupleId));
    if (!couple) return "";
    return [couple.hisEmail, couple.herEmail]
      .map((e) => (e || "").trim())
      .filter(Boolean)
      .join(" · ");
  };

  const oversightOptions = useMemo(
    () => oversightOptionsForType(retreatType),
    [retreatType],
  );

  const showSeedFunding = retreatType === "New Parish / First Retreat";
  const showOutreachBudget = retreatType === "Archbishop Outreach Retreat";

  const budgetTotal = useMemo(() => {
    let sum = 0;
    let any = false;
    for (const line of BUDGET_LINE_KEYS) {
      const raw = budget[line.key];
      if (raw === "" || raw == null) continue;
      const n = Number(raw);
      if (!Number.isNaN(n)) {
        sum += n;
        any = true;
      }
    }
    return any ? sum : null;
  }, [budget]);

  const eligibilityCount = useMemo(
    () => ELIGIBILITY_ITEMS.filter((i) => eligibility[i.id]?.checked).length,
    [eligibility],
  );
  const eligibilityComplete = eligibilityCount === ELIGIBILITY_ITEMS.length;

  const primarySupport = useMemo(() => {
    const matches = planningSupport.filter((r) => r.supportType === supportType);
    return matches.length ? matches[matches.length - 1] : null;
  }, [planningSupport, supportType]);

  const primarySupportName = useMemo(
    () => resolveSupportName(primarySupport) || "Not assigned",
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [primarySupport, couples, supportPeopleById],
  );

  const planningReadiness = useMemo(() => {
    const items = [
      { key: "parish", ok: Boolean(parishId) },
      { key: "language", ok: Boolean(language) },
      { key: "retreatType", ok: Boolean(retreatType) },
      { key: "seasonYear", ok: Boolean(season && year) },
      { key: "planningContact", ok: Boolean(primarySupport) },
      { key: "leadCouple", ok: Boolean(leadCoupleId) },
      {
        key: "eligibility",
        ok: Boolean(leadCoupleId) && eligibilityComplete,
      },
      { key: "tentativeFriday", ok: Boolean(tentativeFriday) },
      {
        key: "transitionNote",
        ok: Boolean(String(transitionNote || "").trim()),
      },
    ];
    const complete = items.filter((i) => i.ok).length;
    return {
      percent: Math.round((complete / items.length) * 100),
      complete,
      total: items.length,
      items,
    };
  }, [
    parishId,
    language,
    retreatType,
    season,
    year,
    primarySupport,
    leadCoupleId,
    eligibilityComplete,
    tentativeFriday,
    transitionNote,
  ]);

  const transitionMissing = useMemo(() => {
    const missing: string[] = [];
    if (!parishId) missing.push("Parish");
    if (!language) missing.push("Language");
    if (!retreatType) missing.push("Retreat type");
    if (!season || !year) missing.push("Season and year");
    if (!supportType) missing.push("Planning oversight");
    if (!leadCoupleId) missing.push("Lead Couple");
    if (!primarySupport) missing.push("Planning contact for selected oversight");
    if (!eligibilityComplete) missing.push("Lead Couple eligibility review");
    if (String(transitionNote || "").trim().length < 3) missing.push("Handoff note");
    if (String(notificationMessage || "").trim().length < 20) {
      missing.push("Notification message");
    }
    return missing;
  }, [
    parishId,
    language,
    retreatType,
    season,
    year,
    supportType,
    leadCoupleId,
    primarySupport,
    eligibilityComplete,
    transitionNote,
    notificationMessage,
  ]);

  const markDirty = () => setDirty(true);

  const refreshParishes = async () => {
    const parishRes: any = await authApi.getPerishList();
    if (Array.isArray(parishRes?.data)) setParishes(parishRes.data);
  };

  const refreshCouples = async () => {
    const coupleRes: any = await adminApi.allCouples();
    const list =
      coupleRes?.success && Array.isArray(coupleRes.data)
        ? coupleRes.data
        : Array.isArray(coupleRes?.data)
          ? coupleRes.data
          : [];
    setCouples(list);
    return list as CoupleRow[];
  };

  const refreshSteeringMembers = async () => {
    try {
      const res: any = await committeeApi.getAllCommitteMembers({});
      if (res?.success) {
        setSteeringMembers(flattenSteeringMembers(res.data));
      }
    } catch {
      setSteeringMembers([]);
    }
  };

  const refreshSuperAdmins = async () => {
    try {
      const res: any = await adminApi.geAdminsAndSubAdmins();
      if (res?.success) {
        setSuperAdmins(flattenSuperAdmins(res.admins));
      }
    } catch {
      setSuperAdmins([]);
    }
  };

  useEffect(() => {
    setPendingContactValue(null);
  }, [parishId, supportType]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const [, coupleList] = await Promise.all([
          refreshParishes(),
          refreshCouples(),
          refreshSteeringMembers(),
          refreshSuperAdmins(),
        ]);
        if (cancelled) return;
        if (retreatId) {
          const detailRes: any = await retreatApi.getRetreatSetupDetail({
            id: retreatId,
          });
          if (!detailRes?.success) {
            toast.error(detailRes?.msg || "Failed to load projected retreat.");
            return;
          }
          const data = detailRes.data;
          if (data.lifecycleStatus && data.lifecycleStatus !== "Projected") {
            toast.info("This retreat is no longer Projected.");
            navigate(`/retreat-setup/${retreatId}`, { replace: true });
            return;
          }
          setParishId(data.parishId ? Number(data.parishId) : "");
          setVenue(data.venue || "");
          setLanguage(data.language || "English");
          setRetreatType(data.retreatType || "Established Parish");
          setSeason((data.season as Season) || "");
          setYear(data.seasonYear ? String(data.seasonYear) : "");
          if (data.dateFrom) {
            const fri = parseNyCalendarDate(data.dateFrom);
            if (fri) {
              const sun = data.dateTo
                ? parseNyCalendarDate(data.dateTo) || addDays(fri, 2)
                : addDays(fri, 2);
              setTentativeFriday(fri);
              setTentativeSunday(sun);
              setDateLocksSeasonYear(true);
            }
          }
          const nextSupport =
            (data.supportType as SupportTypeCode) ||
            oversightOptionsForType(data.retreatType || "")[0] ||
            SUPPORT_TYPES.STEERING_COMMITTEE;
          setSupportType(nextSupport);
          setPlanningSupport(
            Array.isArray(data.planningSupport) ? data.planningSupport : [],
          );
          const notesList = Array.isArray(data.planningNotesList)
            ? data.planningNotesList
            : [];
          setPlanningNotes(
            notesList[0]?.text ||
              (typeof data.planningNotes === "string" &&
              !String(data.planningNotes).startsWith("[")
                ? data.planningNotes
                : "") ||
              "",
          );
          const fp = data.financialPlan || {};
          if (data.retreatType === "New Parish / First Retreat") {
            setSeedStatus(fp.status || "Not reviewed");
            setSeedAmount(
              fp.seedAmount === 0 || fp.seedAmount
                ? String(fp.seedAmount)
                : "",
            );
            setSeedFundingContact(fp.fundingContact || "");
            setSeedExecutionContact(fp.executionContact || "");
            setSeedNote(fp.coordinationNote || "");
          } else if (data.retreatType === "Archbishop Outreach Retreat") {
            setSponsorshipStatus(fp.status || "Not reviewed");
            setSponsorshipContact(fp.fundingContact || "");
            setSponsorshipExecution(fp.executionContact || "");
            setSponsorshipNote(fp.coordinationNote || "");
            setBudgetNote(fp.budgetNote || "");
            const pb = fp.proposedBudget || {};
            setBudget({
              REGISTRATIONS:
                pb.REGISTRATIONS === 0 || pb.REGISTRATIONS
                  ? String(pb.REGISTRATIONS)
                  : "",
              TRANSPORTATION:
                pb.TRANSPORTATION === 0 || pb.TRANSPORTATION
                  ? String(pb.TRANSPORTATION)
                  : "",
              CATERING:
                pb.CATERING === 0 || pb.CATERING ? String(pb.CATERING) : "",
              LODGING:
                pb.LODGING === 0 || pb.LODGING ? String(pb.LODGING) : "",
              MATERIALS:
                pb.MATERIALS === 0 || pb.MATERIALS ? String(pb.MATERIALS) : "",
              OTHER: pb.OTHER === 0 || pb.OTHER ? String(pb.OTHER) : "",
            });
          }
          setLeadCoupleId(data.leadCoupleId ? Number(data.leadCoupleId) : "");
          const elig = emptyEligibility();
          (data.leadEligibility || []).forEach((row: any) => {
            if (row?.id && elig[row.id]) {
              elig[row.id] = {
                checked: Boolean(row.verified),
                note: row.note || "",
                showNote: Boolean(row.note),
              };
            }
          });
          setEligibility(elig);
          setTransitionNote(data.transitionNote || "");
          setNotificationMessage(data.notificationMessage || "");
          setNotificationEdited(Boolean(data.notificationEdited));
          setSavedPlanningPercent(
            data.planningReadinessPercent != null
              ? Number(data.planningReadinessPercent)
              : null,
          );
          setDirty(false);
          void coupleList;
        }
      } catch (err: any) {
        toast.error(err?.toString?.() || "Failed to load form data.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retreatId]);

  const applyRetreatType = (type: string) => {
    setRetreatType(type);
    markDirty();
    const options = oversightOptionsForType(type);
    if (!options.includes(supportType)) {
      setSupportType(options[0]);
      setPendingContactValue(null);
    }
  };

  const clearTentativeDate = () => {
    setTentativeFriday(null);
    setTentativeSunday(null);
    setDateError("");
    setDateLocksSeasonYear(false);
  };

  const handleFridayChange = (date: Date | null) => {
    markDirty();
    setDateError("");
    if (!date) {
      clearTentativeDate();
      return;
    }
    const noon = toLocalNoon(date);
    if (noon.getDay() !== 5) {
      setDateError("Tentative start date must be a Friday.");
      return;
    }
    setTentativeFriday(noon);
    setTentativeSunday(addDays(noon, 2));
    setSeason(seasonFromMonth(noon.getMonth()));
    setYear(String(seasonYearFromDate(noon)));
    setDateLocksSeasonYear(true);
  };

  const handleSeasonChange = (next: Season) => {
    markDirty();
    setSeason(next);
    if (!year) setYear(String(new Date().getFullYear()));
  };

  const handleYearChange = (next: string) => {
    markDirty();
    setYear(next);
  };

  const calendarOpenToDate = useMemo(() => {
    if (season && year) {
      const yearNum = Number(year);
      if (!Number.isNaN(yearNum)) {
        return seasonDateRange(season as Season, yearNum).start;
      }
    }
    return tentativeFriday || undefined;
  }, [season, year, tentativeFriday]);

  const buildFinancialPlan = () => {
    if (showSeedFunding) {
      return {
        status: seedStatus,
        seedAmount: seedAmount === "" ? null : Number(seedAmount),
        fundingContact: seedFundingContact,
        executionContact: seedExecutionContact,
        coordinationNote: seedNote,
        proposedBudget: null,
        budgetNote: "",
      };
    }
    if (showOutreachBudget) {
      const proposedBudget: Record<string, number | null> = {};
      for (const line of BUDGET_LINE_KEYS) {
        const raw = budget[line.key];
        proposedBudget[line.key] = raw === "" ? null : Number(raw);
      }
      return {
        status: sponsorshipStatus,
        seedAmount: null,
        fundingContact: sponsorshipContact,
        executionContact: sponsorshipExecution,
        coordinationNote: sponsorshipNote,
        proposedBudget,
        budgetNote,
      };
    }
    return null;
  };

  const buildLeadEligibility = () =>
    ELIGIBILITY_ITEMS.map((item) => ({
      id: item.id,
      verified: Boolean(eligibility[item.id]?.checked),
      note: eligibility[item.id]?.note || "",
    }));

  const buildAutoNotification = () => {
    const parishName = parishDisplayName(selectedParish) || "Parish not selected";
    const seasonYear =
      season && year ? `${season} ${year}` : "Season/year not set";
    const weekend =
      tentativeFriday && tentativeSunday
        ? formatWeekendRange(tentativeFriday, tentativeSunday)
        : "Exact weekend remains tentative";
    const lead = selectedLead
      ? coupleShortName(selectedLead)
      : "Not assigned";
    const contact = resolveSupportName(primarySupport) || "Not assigned";
    const oversight =
      SUPPORT_LABELS[supportType] || supportType || "Not selected";
    return (
      `Hello ${lead} and ${contact},\n\n` +
      `${parishName}'s ${seasonYear} LOVESTRONG retreat is entering Retreats in Setup. ` +
      `The tentative weekend is ${weekend}. Planning oversight is ${oversight} — ${contact}.\n\n` +
      `Planning context:\n${planningNotes || "(none)"}\n\n` +
      `Lead and Planning Contact access-assignment preparation will be recorded with this transition. ` +
      `This draft is prepared for review only. No production email will be sent.`
    );
  };

  const rebuildNotification = (force = false) => {
    if (notificationEdited && !force) {
      toast.info("Notification was edited. Use Rebuild draft to overwrite.");
      return;
    }
    setNotificationMessage(buildAutoNotification());
    if (force) setNotificationEdited(false);
    markDirty();
  };

  const buildPayload = (extra: Record<string, unknown> = {}) => {
    const location = selectedParish
      ? [selectedParish.city, selectedParish.state].filter(Boolean).join(", ")
      : "";
    return {
      ...actorPayload(),
      parishId: parishId || null,
      language,
      retreatType,
      location,
      venue,
      season: season || null,
      seasonYear: year ? Number(year) : null,
      tentativeFriday: tentativeFriday
        ? calendarDateString(tentativeFriday)
        : null,
      clearTentativeDate: !tentativeFriday,
      supportType,
      planningSupport,
      financialPlan: buildFinancialPlan(),
      leadCoupleId: leadCoupleId || null,
      leadEligibility: buildLeadEligibility(),
      planningNotes,
      transitionNote,
      notificationMessage,
      notificationEdited,
      autoNotificationMessage: buildAutoNotification(),
      ...extra,
    };
  };

  const handleSave = async () => {
    if (!parishId || !language || !retreatType) {
      toast.error("Parish, language, and retreat type are required.");
      return;
    }
    if (dateError) {
      toast.error(dateError);
      return;
    }
    setSaving(true);
    try {
      const payload = buildPayload({
        createdBySuperAdmin: isAdmin,
      });
      let res: any;
      if (retreatId) {
        res = await retreatApi.updateProjectedRetreat({
          id: retreatId,
          ...payload,
        });
      } else {
        res = await retreatApi.createProjectedRetreat(payload);
      }
      if (!res?.success) {
        toast.error(res?.msg || "Save failed.");
        return;
      }
      toast.success(res.msg || "Projected retreat saved.");
      setDirty(false);
      const newId = res?.data?.id || retreatId;
      if (res?.data?.planningReadinessPercent != null) {
        setSavedPlanningPercent(Number(res.data.planningReadinessPercent));
      } else {
        setSavedPlanningPercent(planningReadiness.percent);
      }
      if (!retreatId && newId) {
        navigate(`/retreats/${newId}/project`, { replace: true });
      }
    } catch (err: any) {
      toast.error(err?.toString?.() || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const handleAddSupport = () => {
    const parsed = parseContactOptionValue(pendingContactValue);
    if (!parsed) {
      toast.error("Select a planning contact to add.");
      return;
    }
    const exists = planningSupport.some((r) => {
      if (r.supportType !== supportType) return false;
      if (parsed.kind === "user") return Number(r.userId) === parsed.id;
      return Number(r.coupleId) === parsed.id;
    });
    if (exists) {
      toast.info("That contact is already listed for this oversight type.");
      return;
    }
    const option = contactOptions.find((o) => o.value === pendingContactValue);
    const displayName =
      option?.label ||
      (parsed.kind === "couple"
        ? coupleShortName(
            couples.find((c) => Number(c.id) === parsed.id) || null,
          )
        : formatPersonName(supportPeopleById.get(parsed.id) || null));
    setPlanningSupport((prev) => [
      ...prev,
      parsed.kind === "user"
        ? {
            userId: parsed.id,
            supportType,
            addedAt: new Date().toISOString(),
            coupleName: displayName || undefined,
          }
        : {
            coupleId: parsed.id,
            supportType,
            addedAt: new Date().toISOString(),
            coupleName: displayName || undefined,
          },
    ]);
    setPendingContactValue(null);
    markDirty();
  };

  const handleSaveNewParish = async (forceSave = false) => {
    const errs: Record<string, string> = {};
    if (!newParishForm.name.trim()) errs.name = "Parish name is required.";
    if (!newParishForm.addressLine1.trim())
      errs.addressLine1 = "Address is required.";
    if (!newParishForm.city.trim()) errs.city = "City is required.";
    if (Object.keys(errs).length) {
      setNewParishErrors(errs);
      return;
    }
    setNewParishSaving(true);
    try {
      const fd = new FormData();
      fd.append(
        "fromData",
        JSON.stringify({
          parish: newParishForm.name.trim(),
          address: newParishForm.addressLine1.trim(),
          city: newParishForm.city.trim(),
          state: newParishForm.state.trim() || "TX",
          zip: newParishForm.postalCode.trim(),
          forceSave,
        }),
      );
      const res: any = await parishApi.addPerishWithRegistrationAccess(fd);
      if (!res?.success) {
        toast.error(res?.msg || "Failed to create parish.");
        return;
      }
      if (res.matches?.length && !forceSave) {
        if (
          window.confirm(
            "Similar parish(es) found. Create anyway?",
          )
        ) {
          await handleSaveNewParish(true);
        }
        return;
      }
      await refreshParishes();
      const createdId = res?.data?.id;
      if (createdId) {
        setParishId(Number(createdId));
        markDirty();
      }
      setNewParishModalOpen(false);
      setNewParishForm({
        name: "",
        addressLine1: "",
        city: "",
        state: "TX",
        postalCode: "",
      });
      setNewParishErrors({});
      toast.success("Parish created.");
    } catch (err: any) {
      toast.error(err?.toString?.() || "Failed to create parish.");
    } finally {
      setNewParishSaving(false);
    }
  };

  const openLeadContactEdit = () => {
    if (!selectedLead) return;
    setLeadContactForm({
      hisFirstName: selectedLead.hisFirstName || "",
      hisLastName: selectedLead.hisLastName || "",
      hisEmail: selectedLead.hisEmail || "",
      herFirstName: selectedLead.herFirstName || "",
      herLastName: selectedLead.herLastName || "",
      herEmail: selectedLead.herEmail || "",
      notes: selectedLead.notes || "",
      address: selectedLead.address || "",
    });
    setLeadContactModalOpen(true);
  };

  const handleSaveLeadContact = async () => {
    if (!selectedLead) return;
    setLeadContactSaving(true);
    try {
      const fd = new FormData();
      fd.append(
        "fromData",
        JSON.stringify({
          id: selectedLead.id,
          hisFirstName: leadContactForm.hisFirstName.trim(),
          hisLastName: leadContactForm.hisLastName.trim(),
          hisEmail: leadContactForm.hisEmail.trim(),
          herFirstName: leadContactForm.herFirstName.trim(),
          herLastName: leadContactForm.herLastName.trim(),
          herEmail: leadContactForm.herEmail.trim(),
          notes: leadContactForm.notes.trim(),
          address: leadContactForm.address.trim(),
        }),
      );
      const api = isAdmin ? adminApi : authApi;
      const res: any = await api.editCouple(fd);
      if (!res?.success) {
        toast.error(res?.msg || "Failed to update contact.");
        return;
      }
      await refreshCouples();
      toast.success("Contact information updated.");
      setLeadContactModalOpen(false);
    } catch (err: any) {
      toast.error(err?.toString?.() || "Failed to update contact.");
    } finally {
      setLeadContactSaving(false);
    }
  };

  const handleConfirmTransition = async () => {
    if (!retreatId) {
      toast.error("Save the projected retreat before moving to Setup.");
      return;
    }
    if (transitionMissing.length) {
      toast.error(`Complete: ${transitionMissing.join(", ")}`);
      return;
    }
    setTransitioning(true);
    try {
      const saveRes: any = await retreatApi.updateProjectedRetreat({
        id: retreatId,
        ...buildPayload(),
      });
      if (!saveRes?.success) {
        toast.error(saveRes?.msg || "Could not save before transition.");
        return;
      }
      const res: any = await retreatApi.startRetreatSetup({
        id: retreatId,
        confirm: true,
        transitionNote,
        notificationMessage,
        notificationEdited,
        leadEligibility: buildLeadEligibility(),
        planningSupport,
        supportType,
        ...actorPayload(),
      });
      if (!res?.success) {
        toast.error(res?.msg || "Transition failed.");
        return;
      }
      setTransitionSuccess({
        parishName:
          parishDisplayName(selectedParish) || "This retreat",
        retreatId,
        accessPreparedAt:
          res.notification?.accessPreparedAt || new Date(),
      });
      setDirty(false);
      toast.success("Moved projected retreat to Retreats in Setup.");
    } catch (err: any) {
      toast.error(err?.toString?.() || "Transition failed.");
    } finally {
      setTransitioning(false);
      setSetupConfirmModalOpen(false);
    }
  };

  return {
    navigate,
    retreatId,
    isAdmin,
    loading,
    saving,
    transitioning,
    parishes,
    couples,
    parishId,
    setParishId,
    venue,
    setVenue,
    language,
    setLanguage,
    retreatType,
    season,
    setSeason,
    year,
    setYear,
    tentativeFriday,
    tentativeSunday,
    dateError,
    dateLocksSeasonYear,
    calendarOpenToDate,
    supportType,
    setSupportType,
    pendingContactValue,
    setPendingContactValue,
    planningSupport,
    setPlanningSupport,
    planningNotes,
    setPlanningNotes,
    seedStatus,
    setSeedStatus,
    seedAmount,
    setSeedAmount,
    seedFundingContact,
    setSeedFundingContact,
    seedExecutionContact,
    setSeedExecutionContact,
    seedNote,
    setSeedNote,
    sponsorshipStatus,
    setSponsorshipStatus,
    sponsorshipContact,
    setSponsorshipContact,
    sponsorshipExecution,
    setSponsorshipExecution,
    sponsorshipNote,
    setSponsorshipNote,
    budgetNote,
    setBudgetNote,
    budget,
    setBudget,
    leadCoupleId,
    setLeadCoupleId,
    eligibility,
    setEligibility,
    nextStage,
    setNextStage,
    transitionNote,
    setTransitionNote,
    notificationMessage,
    setNotificationMessage,
    notificationEdited,
    setNotificationEdited,
    dirty,
    savedPlanningPercent,
    newParishModalOpen,
    setNewParishModalOpen,
    newParishSaving,
    newParishForm,
    setNewParishForm,
    newParishErrors,
    setNewParishErrors,
    leadContactModalOpen,
    setLeadContactModalOpen,
    leadContactSaving,
    leadContactForm,
    setLeadContactForm,
    reportModalOpen,
    setReportModalOpen,
    setupConfirmModalOpen,
    setSetupConfirmModalOpen,
    transitionSuccess,
    selectedParish,
    selectedLead,
    leadAssigned,
    coupleOptions,
    contactOptions,
    oversightOptions,
    oversightPickerHelper: oversightContactHelper(supportType, parishId),
    oversightPickerPlaceholder: oversightContactPlaceholder(supportType),
    oversightSubtitle: oversightSectionSubtitle(supportType),
    showSeedFunding,
    showOutreachBudget,
    budgetTotal,
    eligibilityCount,
    eligibilityComplete,
    primarySupport,
    primarySupportName,
    resolveSupportName,
    resolveSupportEmail,
    planningReadiness,
    transitionMissing,
    markDirty,
    applyRetreatType,
    handleFridayChange,
    handleSeasonChange,
    handleYearChange,
    rebuildNotification,
    handleSave,
    handleAddSupport,
    handleSaveNewParish,
    openLeadContactEdit,
    handleSaveLeadContact,
    handleConfirmTransition,
    displayPercent: dirty
      ? planningReadiness.percent
      : savedPlanningPercent ?? planningReadiness.percent,
    saveStatusLabel: dirty
      ? "Unsaved changes"
      : retreatId
        ? "Saved"
        : "Not yet saved",
    saveStatusShort: dirty
      ? "Unsaved"
      : retreatId
        ? "Saved"
        : "Not saved",
  };
}

export type ProjectedRetreatFormModel = ReturnType<
  typeof useProjectedRetreatForm
>;
